/**
 * Thursday Night Fights — tnf-event.js
 * Place in: assets/tnf-event.js
 *
 * Requires window.TNF_CONFIG injected by page.tnf-event.liquid:
 *   supabaseUrl    — your Supabase project URL
 *   supabaseKey    — your Supabase anon/public key
 *   customerName   — Shopify customer full name (or null if not logged in)
 *   customerId     — Shopify customer ID (or null)
 *   isAdmin        — true if customer has 'tnf-admin' tag
 */

(function () {
  'use strict';

  /* ─────────────────────────────────────────────
     CONFIG + SUPABASE CLIENT
  ───────────────────────────────────────────── */
  const { supabaseUrl, supabaseKey, customerName, customerId, customerEmail, isAdmin } = window.TNF_CONFIG || {};

  // Lightweight Supabase REST wrapper — no SDK needed
  async function sbFetch(path, options = {}) {
    const url = supabaseUrl.replace(/\/$/, '') + '/rest/v1' + path;
    const res = await fetch(url, {
      ...options,
      headers: {
        'apikey': supabaseKey,
        'Authorization': 'Bearer ' + supabaseKey,
        'Content-Type': 'application/json',
        'Prefer': options.prefer || 'return=representation',
        ...(options.headers || {}),
      },
    });
    if (!res.ok) {
      const err = await res.text();
      throw new Error('Supabase error ' + res.status + ': ' + err);
    }
    const text = await res.text();
    return text ? JSON.parse(text) : null;
  }

  /* ─────────────────────────────────────────────
     WEEK KEY  (Monday-based ISO week string)
  ───────────────────────────────────────────── */
  function getWeekKey(offsetWeeks) {
    const now = new Date();
    // Use local day-of-week to avoid UTC timezone drift
    const day = now.getDay(); // 0=Sun, 1=Mon, ...
    // On Monday, event week is still the PREVIOUS week (Thursday hasn't happened yet).
    // All other days: find most recent Monday normally.
    let diffToMonday;
    // Mon(1), Tue(2), Wed(3), Thu(4) → previous Monday (event week, Thursday not yet passed)
    // Fri(5), Sat(6), Sun(0) → current Monday (sign-up window for next Thursday)
    if (day >= 1 && day <= 4) {
      diffToMonday = 1 - day - 7; // go back to previous Monday
    } else if (day === 0) {
      diffToMonday = -6; // Sunday → this week's Monday
    } else {
      diffToMonday = 1 - day; // Fri(5), Sat(6) → this week's Monday
    }
    const d = new Date(now.getFullYear(), now.getMonth(), now.getDate()); // local midnight, no UTC shift
    d.setDate(d.getDate() + diffToMonday + (offsetWeeks || 0) * 7);
    const yyyy = d.getFullYear();
    const mm   = String(d.getMonth() + 1).padStart(2, '0');
    const dd   = String(d.getDate()).padStart(2, '0');
    return yyyy + '-' + mm + '-' + dd;
  }

  function getThursdayDate(offsetWeeks) {
    // Parse the week key as local date (not UTC) to avoid timezone drift
    const parts = getWeekKey(offsetWeeks).split('-');
    const monday = new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
    // Mon-Thu: getWeekKey returns previous Monday, so Thursday is +10 days
    // Fri-Sun: getWeekKey returns current Monday, so Thursday is +3 days
    const day = new Date().getDay();
    const offset = (day >= 1 && day <= 4) ? 10 : 3;
    monday.setDate(monday.getDate() + offset);
    return monday.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  }

  function thursdayHasPassed() {
    // Returns true on Fri(5), Sat(6), Sun(0) — this week's Thursday is done, next event is upcoming
    // Mon(1) through Thu(4) — still in the active event week
    const day = new Date().getDay();
    return day === 5 || day === 6 || day === 0;
  }

  function weekLabel(offset) {
    const thursday = getThursdayDate(offset);
    if (offset === 0) {
      if (thursdayHasPassed()) return 'Next Thursday — ' + thursday;
      return 'This Week — Thursday ' + thursday; // Mon-Thu: active event week
    }
    if (offset === -1) return 'Last Week — Thursday ' + thursday;
    return 'Thursday ' + thursday;
  }

  /* ─────────────────────────────────────────────
     STATE
  ───────────────────────────────────────────── */
  let selectedStyle    = null;
  let viewWeekOffset   = 0;
  let signupsOpen      = null; // loaded from DB
  let currentWeekKey   = getWeekKey(0);
  let lockedPairs      = []; // { p1, p2 } — session-only, cleared on refresh

  /* ─────────────────────────────────────────────
     DOM HELPERS
  ───────────────────────────────────────────── */
  const $ = id => document.getElementById(id);

  function showToast(msg) {
    const el = $('tnf-toast');
    el.textContent = msg;
    el.classList.add('tnf-toast--show');
    setTimeout(() => el.classList.remove('tnf-toast--show'), 2800);
  }

  function initials(name) {
    return (name || '?').trim().split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
  }

  function escapeHTML(str) {
    return String(str || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  function setStatusPill(state) {
    const pill = $('tnf-status-pill');
    pill.className = 'tnf-status-pill';
    if (state === 'open')    { pill.classList.add('tnf-status-pill--open');    pill.textContent = 'Sign-ups Open'; }
    if (state === 'closed')  { pill.classList.add('tnf-status-pill--closed');  pill.textContent = 'Sign-ups Closed'; }
    if (state === 'matched') { pill.classList.add('tnf-status-pill--matched'); pill.textContent = 'Pairings Up!'; }
    if (state === 'loading') { pill.classList.add('tnf-status-pill--loading'); pill.textContent = 'Loading…'; }
  }

  function updateOverrideIndicator(isOverride) {
    const el = $('tnf-admin-override-note');
    if (!el) return;
    el.style.display = isOverride ? 'block' : 'none';
  }

  /* ─────────────────────────────────────────────
     TABS
  ───────────────────────────────────────────── */
  document.querySelectorAll('.tnf-tab').forEach(tab => {
    tab.addEventListener('click', () => {
      document.querySelectorAll('.tnf-tab').forEach(t => t.classList.remove('tnf-tab--active'));
      document.querySelectorAll('.tnf-panel').forEach(p => p.classList.remove('tnf-panel--active'));
      tab.classList.add('tnf-tab--active');
      $('tnf-panel-' + tab.dataset.tab).classList.add('tnf-panel--active');
      if (tab.dataset.tab === 'signup')   initSignupTab();
      if (tab.dataset.tab === 'players')  loadPlayersTab();
      if (tab.dataset.tab === 'pairings') loadPairingsTab();
      if (tab.dataset.tab === 'admin')    loadAdminTab();
    });
  });

  /* ─────────────────────────────────────────────
     STYLE SELECTOR
  ───────────────────────────────────────────── */
  document.querySelectorAll('.tnf-style-btn').forEach(btn => {
    btn.addEventListener('click', () => {
      const s = btn.dataset.style;
      selectedStyle = s;
      document.querySelectorAll('.tnf-style-btn').forEach(b => {
        b.className = 'tnf-style-btn';
        if (b.dataset.style === s) b.classList.add('tnf-style-btn--selected-' + s);
      });
      const leagueNote    = $('tnf-league-note');
      const narrativeWrap = $('tnf-narrative-wrap');
      if (leagueNote)    leagueNote.style.display    = s === 'league'    ? 'block' : 'none';
      if (narrativeWrap) narrativeWrap.style.display = s === 'narrative' ? 'block' : 'none';
    });
  });

  /* ─────────────────────────────────────────────
     SCHEDULE — derive open/closed from day of week
     Fri (5), Sat (6), Sun (0) → open
     Mon (1) → closed (pairings day)
     Tue (2), Wed (3), Thu (4) → closed (event week)
     Admin toggle overrides this for the current week.
  ───────────────────────────────────────────── */
  function scheduledSignupsOpen() {
    const day = new Date().getDay(); // 0=Sun,1=Mon,...,6=Sat
    return day === 0 || day === 5 || day === 6; // Sun, Fri, Sat
  }

  /* ─────────────────────────────────────────────
     EVENT SETTINGS  (signups open/closed + week state)
  ───────────────────────────────────────────── */
  async function loadEventSettings() {
    try {
      const rows = await sbFetch('/tnf_event_settings?week_key=eq.' + currentWeekKey + '&limit=1');

      if (rows && rows.length > 0) {
        const row = rows[0];
        const day = new Date().getDay();

        // Schedule enforcement — only applies when admin hasn't taken manual control.
        // admin_override persists for the life of this week_key row; a new Friday
        // rolls over to a fresh row with admin_override=false, resuming schedule.
        if (!row.admin_override && !row.matched) {
          // Mon-Thu: force closed. Monday is pairings day; Tue-Thu is event lockdown.
          if (day >= 1 && day <= 4 && row.signups_open) {
            await sbFetch('/tnf_event_settings?week_key=eq.' + currentWeekKey, {
              method: 'PATCH',
              body: JSON.stringify({ signups_open: false }),
            });
            return { ...row, signups_open: false };
          }
          // Fri-Sun: force open. Sign-up window for next Thursday.
          if ((day === 0 || day === 5 || day === 6) && !row.signups_open) {
            await sbFetch('/tnf_event_settings?week_key=eq.' + currentWeekKey, {
              method: 'PATCH',
              body: JSON.stringify({ signups_open: true }),
            });
            return { ...row, signups_open: true };
          }
        }
        return row;
      }

      // No row yet for this week — create one using the schedule as the default.
      const scheduled = scheduledSignupsOpen();
      const created = await sbFetch('/tnf_event_settings', {
        method: 'POST',
        body: JSON.stringify({ week_key: currentWeekKey, signups_open: scheduled, matched: false, admin_override: false }),
      });
      return created && created[0] ? created[0] : { signups_open: scheduled, matched: false, admin_override: false };
    } catch (e) {
      console.error('TNF: loadEventSettings failed', e);
      return { signups_open: scheduledSignupsOpen(), matched: false };
    }
  }

  async function saveEventSettings(patch) {
    return sbFetch('/tnf_event_settings?week_key=eq.' + currentWeekKey, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    });
  }

  /* ─────────────────────────────────────────────
     SIGN-UP TAB — INIT
  ───────────────────────────────────────────── */
  let currentSignup = null; // the player's own signup record for this week

  async function initSignupTab() {
    setStatusPill('loading');
    const settings = await loadEventSettings();
    signupsOpen = settings.signups_open;

    if (settings.matched) {
      setStatusPill('matched');
    } else {
      setStatusPill(signupsOpen ? 'open' : 'closed');
    }

    if (!customerName) return; // not logged in — static liquid handles the message

    const openEl   = $('tnf-signup-open');
    const closedEl = $('tnf-signup-closed');
    if (!openEl || !closedEl) return;

    if (!signupsOpen && !settings.matched) {
      openEl.style.display = 'none';
      closedEl.style.display = 'block';
      return;
    }

    // If pairings are done, check if this player is registered before showing the open panel
    if (settings.matched) {
      const existing = await sbFetch(
        '/tnf_signups?week_key=eq.' + currentWeekKey + '&customer_id=eq.' + customerId + '&limit=1'
      );
      if (!existing || existing.length === 0) {
        // Not registered — show closed panel, pairings already ran
        openEl.style.display = 'none';
        closedEl.style.display = 'block';
        $('tnf-signup-closed').innerHTML = '<div class="tnf-card tnf-card--center"><div class="tnf-empty-icon">⚔️</div><p class="tnf-empty-text">Pairings have been announced.<br>Sign-ups are now closed.</p></div>';
        return;
      }
    }

    openEl.style.display = 'block';
    closedEl.style.display = 'none';

    // Populate game system dropdown
    const gameSelect = $('tnf-signup-game');
    if (gameSelect) {
      try {
        const [systems, profiles] = await Promise.all([
          sbFetch('/tnf_game_systems?active=eq.true&order=display_order.asc,name.asc'),
          customerId ? sbFetch('/tnf_player_profiles?customer_id=eq.' + customerId + '&limit=1') : Promise.resolve([]),
        ]);
        gameSelect.innerHTML = '<option value="">— select a game system —</option>' +
          (systems || []).map(gs => `<option value="${gs.id}" data-name="${gs.name}">${gs.name}</option>`).join('');
        // Pre-select from profile if set, otherwise default to Warhammer 40,000
        const profile = profiles && profiles[0] ? profiles[0] : null;
        if (profile && profile.favourite_game_id) {
          gameSelect.value = String(profile.favourite_game_id);
        } else {
          // Find Warhammer 40,000 by name and select it
          const warhammer = (systems || []).find(gs => gs.name === 'Warhammer 40,000');
          if (warhammer) gameSelect.value = String(warhammer.id);
        }
      } catch (e) {
        gameSelect.innerHTML = '<option value="">Could not load game systems</option>';
      }
    }

    // Check if already registered
    const existing = await sbFetch(
      '/tnf_signups?week_key=eq.' + currentWeekKey + '&customer_id=eq.' + customerId + '&limit=1'
    );
    if (existing && existing.length > 0) {
      currentSignup = existing[0];
      const alreadyEl = $('tnf-already-registered');
      const formEl    = $('tnf-signup-form-wrap');
      if (alreadyEl) alreadyEl.style.display = 'block';
      if (formEl)    formEl.style.display = 'none';
      await renderRegisteredCard(settings);
    }
  }

  async function renderRegisteredCard(settings) {
    const cardEl = $('tnf-registered-card');
    if (!cardEl) return;

    const thursdayStr = getThursdayDate(thursdayHasPassed() ? 1 : 0);

    // Load player profile for game system
    const [profiles, gameSystems] = await Promise.all([
      sbFetch('/tnf_player_profiles?customer_id=eq.' + customerId + '&limit=1').catch(() => []),
      sbFetch('/tnf_game_systems?active=eq.true').catch(() => []),
    ]);
    const profile  = profiles && profiles[0] ? profiles[0] : null;
    const gameMap  = {};
    (gameSystems || []).forEach(gs => { gameMap[gs.id] = gs.name; });
    const gameName = profile && profile.favourite_game_id ? gameMap[profile.favourite_game_id] : null;

    if (settings.matched) {
      // Load pairing for this player
      const pairings = await sbFetch(
        '/tnf_pairings?week_key=eq.' + currentWeekKey +
        '&or=(p1_id.eq.' + customerId + ',p2_id.eq.' + customerId + ')'
      ).catch(() => []);
      const pairing = pairings && pairings[0] ? pairings[0] : null;

      if (pairing) {
        const oppId    = pairing.p1_id === String(customerId) ? pairing.p2_id : pairing.p1_id;
        const oppName  = pairing.p1_id === String(customerId) ? pairing.p2_name : pairing.p1_name;
        const oppStyle = pairing.p1_id === String(customerId) ? pairing.p2_style : pairing.p1_style;
        const oppGameName = pairing.p1_id === String(customerId) ? pairing.p2_game_system : pairing.p1_game_system;
        const myGameName  = pairing.p1_id === String(customerId) ? pairing.p1_game_system : pairing.p2_game_system;

        // Load opponent profile for Discord handle
        const oppProfiles = await sbFetch('/tnf_player_profiles?customer_id=eq.' + oppId + '&limit=1').catch(() => []);
        const oppProfile  = oppProfiles && oppProfiles[0] ? oppProfiles[0] : null;
        const oppDiscord  = (oppProfile && oppProfile.discord_handle && oppProfile.show_discord !== false)
          ? `<div style="font-size:11px;color:#534AB7;margin-top:3px;">🎮 ${escapeHTML(oppProfile.discord_handle)}</div>` : '';
        const myDiscord   = (profile && profile.discord_handle && profile.show_discord !== false)
          ? `<div style="font-size:11px;color:#534AB7;margin-top:3px;">🎮 ${escapeHTML(profile.discord_handle)}</div>` : '';
        const myGameLine  = (myGameName || gameName)
          ? `<div style="font-size:11px;color:#888;margin-top:2px;">${escapeHTML(myGameName || gameName)}</div>` : '';
        const oppGameLine = oppGameName
          ? `<div style="font-size:11px;color:#888;margin-top:2px;">${escapeHTML(oppGameName)}</div>` : '';

        cardEl.innerHTML = `
          <div class="tnf-card tnf-registered-card">
            <div class="tnf-registered-header">
              <div>
                <div class="tnf-section-label">Your Pairing — Thursday ${thursdayStr}</div>
              </div>
              <button class="tnf-btn tnf-btn--dropout" id="tnf-dropout-btn">Drop Out</button>
            </div>
            <div class="tnf-match-card" style="margin-top:14px;margin-bottom:0;">
              <div class="tnf-match-player">
                <div class="tnf-match-avatar tnf-avatar--${currentSignup.play_style}">${initials(customerName)}</div>
                <div class="tnf-match-name">${escapeHTML(customerName)}</div>
                <span class="tnf-badge tnf-badge--${currentSignup.play_style}" style="margin-top:4px;display:inline-block;">${currentSignup.play_style}</span>
                ${myGameLine}${myDiscord}
              </div>
              <div class="tnf-match-vs">
                <span class="tnf-vs-label">VS</span>
                <span class="tnf-table-num">T${pairing.table_number}</span>
              </div>
              <div class="tnf-match-player">
                <div class="tnf-match-avatar tnf-avatar--${oppStyle}">${initials(oppName)}</div>
                <div class="tnf-match-name">${escapeHTML(oppName)}</div>
                <span class="tnf-badge tnf-badge--${oppStyle}" style="margin-top:4px;display:inline-block;">${oppStyle}</span>
                ${oppGameLine}${oppDiscord}
              </div>
            </div>
            <div class="tnf-banner tnf-banner--info" style="margin-top:12px;margin-bottom:0;">
              🕠 Games start at <strong>5:30 PM</strong> — contact your opponent to arrange a different time if needed.
            </div>
          </div>`;
      } else {
        // Matched but this player has no pairing (unmatched odd-one-out)
        cardEl.innerHTML = `
          <div class="tnf-card tnf-registered-card">
            <div class="tnf-registered-header">
              <div>
                <div class="tnf-section-label">Registered — Thursday ${thursdayStr}</div>
                ${gameName ? `<div class="tnf-registered-game">${gameName}</div>` : ''}
              </div>
              <button class="tnf-btn tnf-btn--dropout" id="tnf-dropout-btn">Drop Out</button>
            </div>
            <div class="tnf-banner tnf-banner--warn" style="margin-top:12px;margin-bottom:0;">
              Pairings are up but you were not matched this week — please check with the organiser.
            </div>
          </div>`;
      }
    } else {
      // Registered, waiting for pairings
      cardEl.innerHTML = `
        <div class="tnf-card tnf-registered-card">
          <div class="tnf-registered-header">
            <div>
              <div class="tnf-section-label">You're In — Thursday ${thursdayStr}</div>
              ${gameName ? `<div class="tnf-registered-game">${gameName}</div>` : ''}
            </div>
            <button class="tnf-btn tnf-btn--dropout" id="tnf-dropout-btn">Drop Out</button>
          </div>
          <div class="tnf-player-row" style="margin-top:12px;padding-top:0;border-bottom:none;">
            <div class="tnf-avatar tnf-avatar--${currentSignup.play_style}">${initials(customerName)}</div>
            <span class="tnf-player-name">${customerName}</span>
            <span class="tnf-badge tnf-badge--${currentSignup.play_style}">${currentSignup.play_style}</span>
            ${gameName ? `<span class="tnf-badge tnf-badge--game">${gameName}</span>` : ''}
          </div>
          <div class="tnf-banner tnf-banner--info" style="margin-top:12px;margin-bottom:0;">
            ⏳ Pairings announced Monday — check back then to see your opponent.
          </div>
        </div>`;
    }

    // Wire dropout button (rendered dynamically so must attach after render)
    const btn = $('tnf-dropout-btn');
    if (btn) btn.addEventListener('click', handleDropout);
  }

  /* ─────────────────────────────────────────────
     SIGN-UP — DROP OUT
  ───────────────────────────────────────────── */
  async function handleDropout() {
    if (!confirm('Are you sure you want to drop out of this week\'s event?')) return;
    const btn = $('tnf-dropout-btn');
    if (btn) { btn.disabled = true; btn.textContent = 'Dropping out…'; }
    try {
        // Find the player's signup and any pairing
        const signup = currentSignup || (await sbFetch(
          '/tnf_signups?week_key=eq.' + currentWeekKey + '&customer_id=eq.' + customerId + '&limit=1'
        ))?.[0];
        if (!signup) { showToast('Could not find your registration'); return; }

        // Check if they have a pairing
        const pairings = await sbFetch(
          '/tnf_pairings?week_key=eq.' + currentWeekKey +
          '&or=(p1_id.eq.' + customerId + ',p2_id.eq.' + customerId + ')'
        );
        const pairing      = pairings && pairings.length > 0 ? pairings[0] : null;
        const hadPairing   = !!pairing;
        const opponentName = pairing
          ? (pairing.p1_id === String(customerId) ? pairing.p2_name : pairing.p1_name)
          : null;
        const tableNumber  = pairing ? pairing.table_number : null;

        // Remove the pairing if exists
        if (pairing) {
          await sbFetch('/tnf_pairings?id=eq.' + pairing.id, { method: 'DELETE', prefer: 'return=minimal' });
          // Mark opponent as unmatched
          const opponentId = pairing.p1_id === String(customerId) ? pairing.p2_id : pairing.p1_id;
          await sbFetch('/tnf_signups?week_key=eq.' + currentWeekKey + '&customer_id=eq.' + opponentId, {
            method: 'PATCH', prefer: 'return=minimal',
            body: JSON.stringify({ matched: false }),
          });
        }

        // Remove the player's signup
        await sbFetch('/tnf_signups?id=eq.' + signup.id, { method: 'DELETE', prefer: 'return=minimal' });

        // Record dropout in tnf_dropouts table
        await sbFetch('/tnf_dropouts', {
          method: 'POST',
          body: JSON.stringify({
            week_key:      currentWeekKey,
            customer_id:   String(customerId),
            customer_name: customerName,
            had_pairing:   hadPairing,
            opponent_name: opponentName,
            table_number:  tableNumber,
          }),
        });

        // Staff are notified server-side: the dropout row fires the OBG Tools
        // repair automation, which re-pairs and posts to the Discord staff
        // channel. No direct edge-function call from the browser.

        showToast('You have been removed from this week\'s event');
        currentSignup = null;
        const alreadyEl = $('tnf-already-registered');
        const formEl    = $('tnf-signup-form-wrap');
        if (alreadyEl) alreadyEl.style.display = 'none';
        if (formEl)    formEl.style.display = 'block';
        // Reset style selection
        selectedStyle = null;
        document.querySelectorAll('.tnf-style-btn').forEach(b => b.className = 'tnf-style-btn');
      } catch (e) {
        showToast('Drop out failed — please try again');
        console.error('TNF dropout error:', e);
        const btn = $('tnf-dropout-btn');
        if (btn) { btn.disabled = false; btn.textContent = 'Drop Out'; }
      }
  }

  /* ─────────────────────────────────────────────
     SIGN-UP — SUBMIT
  ───────────────────────────────────────────── */
  const signupBtn = $('tnf-signup-btn');
  if (signupBtn) {
    signupBtn.addEventListener('click', async () => {
      if (!selectedStyle) { showToast('Please select how you are playing'); return; }
      const gameSelect     = $('tnf-signup-game');
      const gameSystemId   = gameSelect ? gameSelect.value : '';
      const gameSystemName = gameSelect && gameSelect.selectedIndex > 0
        ? gameSelect.options[gameSelect.selectedIndex].dataset.name : '';
      if (!gameSystemId) { showToast('Please select a game system'); return; }
      const narrativeFaction = selectedStyle === 'narrative'
        ? ($('tnf-narrative-faction') ? $('tnf-narrative-faction').value.trim() : '')
        : '';
      if (selectedStyle === 'narrative' && !narrativeFaction) {
        showToast('Please enter your narrative faction');
        return;
      }
      signupBtn.disabled = true;
      signupBtn.textContent = 'Registering…';
      try {
        await sbFetch('/tnf_signups', {
          method: 'POST',
          body: JSON.stringify({
            week_key:          currentWeekKey,
            customer_id:       String(customerId),
            customer_name:     customerName,
            play_style:        selectedStyle,
            game_system_id:    parseInt(gameSystemId, 10),
            game_system_name:  gameSystemName,
            narrative_faction: narrativeFaction || null,
          }),
        });
        // Increment event signups count on profile
        sbFetch('/tnf_player_profiles?customer_id=eq.' + customerId, {
          method: 'PATCH',
          prefer: 'return=minimal',
          body: JSON.stringify({ event_signups_count: (currentSignup?.event_signups_count || 0) + 1 }),
        }).catch(() => {}); // non-critical
        showToast(selectedStyle === 'league'
          ? 'Table reserved! See you Thursday ⚔️'
          : 'Registered! Pairing announced Monday ⚔️'
        );
        const alreadyEl = $('tnf-already-registered');
        const formEl    = $('tnf-signup-form-wrap');
        if (alreadyEl) alreadyEl.style.display = 'block';
        if (formEl)    formEl.style.display = 'none';
      } catch (e) {
        showToast('Sign-up failed — please try again');
        signupBtn.disabled = false;
        signupBtn.textContent = 'Register for Thursday';
        console.error('TNF signup error:', e);
      }
    });
  }

  /* ─────────────────────────────────────────────
     PLAYERS TAB
  ───────────────────────────────────────────── */
  async function loadPlayersTab() {
    const wk = getWeekKey(viewWeekOffset);
    $('tnf-players-week-label').textContent = weekLabel(viewWeekOffset);
    const list = $('tnf-players-list');
    list.innerHTML = '<p style="color:#aaa;font-size:.875rem;">Loading…</p>';

    const [players, gameSystems] = await Promise.all([
      sbFetch('/tnf_signups?week_key=eq.' + wk + '&order=created_at.asc'),
      sbFetch('/tnf_game_systems?active=eq.true&order=display_order.asc,name.asc'),
    ]);

    if (!players || players.length === 0) {
      list.innerHTML = '<div style="text-align:center;padding:2rem;color:#999;"><div style="font-size:2rem;margin-bottom:8px;">🎲</div><p>No players registered yet.</p></div>';
      return;
    }

    // Load profiles for all signed-up players
    const customerIds = players.map(p => p.customer_id).join(',');
    const profiles = await sbFetch('/tnf_player_profiles?customer_id=in.(' + customerIds + ')');
    const profileMap = {};
    (profiles || []).forEach(pr => { profileMap[pr.customer_id] = pr; });
    const gameMap = {};
    (gameSystems || []).forEach(gs => { gameMap[gs.id] = gs.name; });

    const paired = players.filter(p => p.play_style !== 'league');
    const league = players.filter(p => p.play_style === 'league');
    let html = '';

    if (paired.length) {
      html += '<p class="tnf-section-label">Seeking a pairing (' + paired.length + ')</p>';
      html += paired.map(p => playerRowHTML(p, false, profileMap[p.customer_id], gameMap)).join('');
    }
    if (league.length) {
      if (paired.length) html += '<hr class="tnf-divider">';
      html += '<p class="tnf-section-label">League — table reserved (' + league.length + ')</p>';
      html += league.map(p => playerRowHTML(p, false, profileMap[p.customer_id], gameMap)).join('');
    }
    list.innerHTML = html;
  }

  function playerRowHTML(p, showDelete, profile, gameMap) {
    const del = showDelete
      ? `<button class="tnf-delete-btn" data-id="${p.id}" data-name="${escapeHTML(p.customer_name)}" title="Remove">✕</button>`
      : '';
    const gameName = p.game_system_name ||
      (profile && profile.favourite_game_id && gameMap ? gameMap[profile.favourite_game_id] : null);
    const gameBadge = gameName
      ? `<span class="tnf-badge tnf-badge--game" title="Game system">${escapeHTML(gameName)}</span>`
      : '';
    const discord = (profile && profile.discord_handle && profile.show_discord !== false)
      ? `<span class="tnf-discord-handle">🎮 ${escapeHTML(profile.discord_handle)}</span>`
      : '';
    const faction = p.narrative_faction
      ? `<span style="font-size:11px;color:#854F0B;">📖 ${escapeHTML(p.narrative_faction)}</span>`
      : '';
    return `<div class="tnf-player-row">
      <div class="tnf-avatar tnf-avatar--${p.play_style}">${initials(p.customer_name)}</div>
      <div class="tnf-player-info">
        <span class="tnf-player-name">${escapeHTML(p.customer_name)}</span>
        ${faction}${discord}
      </div>
      ${gameBadge}
      <span class="tnf-badge tnf-badge--${p.play_style}">${p.play_style}</span>
      ${del}
    </div>`;
  }

  const prevBtn = $('tnf-players-prev');
  const nextBtn = $('tnf-players-next');
  if (prevBtn) prevBtn.addEventListener('click', () => { viewWeekOffset--; loadPlayersTab(); });
  if (nextBtn) nextBtn.addEventListener('click', () => { viewWeekOffset++; loadPlayersTab(); });

  /* ─────────────────────────────────────────────
     PAIRINGS TAB
  ───────────────────────────────────────────── */
  async function loadPairingsTab() {
    const wk = getWeekKey(0); // pairings always show current week
    $('tnf-pairings-week-label').textContent = weekLabel(0);
    const list = $('tnf-pairings-list');
    list.innerHTML = '<p style="color:#aaa;font-size:.875rem;">Loading…</p>';

    const [settings, pairings, leaguePlayers] = await Promise.all([
      loadEventSettings(),
      sbFetch('/tnf_pairings?week_key=eq.' + wk + '&order=table_number.asc'),
      sbFetch('/tnf_signups?week_key=eq.' + wk + '&play_style=eq.league&order=created_at.asc'),
    ]);

    if (!settings.matched && (!leaguePlayers || leaguePlayers.length === 0)) {
      list.innerHTML = '<div style="text-align:center;padding:2rem;color:#999;"><div style="font-size:2rem;margin-bottom:8px;">⚔️</div><p>Pairings not yet announced.<br>Check back Monday!</p></div>';
      return;
    }

    // Load profiles for Discord handles
    let profileMap = {};
    if (pairings && pairings.length > 0) {
      const ids = [...new Set(pairings.flatMap(p => [p.p1_id, p.p2_id]))].join(',');
      const profiles = await sbFetch('/tnf_player_profiles?customer_id=in.(' + ids + ')').catch(() => []);
      (profiles || []).forEach(pr => { profileMap[pr.customer_id] = pr; });
    }

    let html = '';
    let nextTable = 1;

    if (settings.matched && pairings && pairings.length > 0) {
      html += '<p class="tnf-section-label">Paired games</p>';
      pairings.forEach(m => {
        html += matchCardHTML(m, nextTable++, profileMap);
      });
    }

    if (leaguePlayers && leaguePlayers.length > 0) {
      if (html) html += '<hr class="tnf-divider">';
      html += '<p class="tnf-section-label">League — reserved tables</p>';
      html += '<div class="tnf-banner tnf-banner--purple" style="margin-bottom:12px;">These players have arranged their own games.</div>';
      leaguePlayers.forEach(p => {
        html += `<div class="tnf-league-card">
          <div class="tnf-avatar tnf-avatar--league">${initials(p.customer_name)}</div>
          <div class="tnf-league-info">
            <div class="tnf-league-name">${p.customer_name}</div>
            <div class="tnf-league-sub">League game</div>
          </div>
          <div class="tnf-table-num">T${nextTable++}</div>
        </div>`;
      });
    }

    if (settings.matched && pairings && pairings.length > 0) {
      const unmatched = await sbFetch('/tnf_signups?week_key=eq.' + wk + '&matched=eq.false&play_style=neq.league');
      if (unmatched && unmatched.length > 0) {
        if (html) html += '<hr class="tnf-divider">';
        html += '<p class="tnf-section-label">Unmatched</p>';
        html += '<div class="tnf-banner tnf-banner--purple" style="margin-bottom:12px;">The following players do not have opponents but are looking for a game. Contact the shop if you would like to match up with them.</div>';
        html += unmatched.map(p => {
          const game = p.game_system_name ? `<div style="font-size:11px;color:#888;margin-top:2px;">${escapeHTML(p.game_system_name)}</div>` : '';
          return `
          <div class="tnf-player-row">
            <div class="tnf-avatar tnf-avatar--${p.play_style}">${initials(p.customer_name)}</div>
            <div style="flex:1;min-width:0;">
              <span class="tnf-player-name">${escapeHTML(p.customer_name)}</span>
              ${game}
            </div>
            <span class="tnf-badge tnf-badge--${p.play_style}">${p.play_style}</span>
          </div>`;
        }).join('');
      }
    }

    list.innerHTML = html || '<div style="text-align:center;padding:2rem;color:#999;">No data yet.</div>';
  }

  function matchCardHTML(m, tableNum, profileMap) {
    const s1 = m.p1_style || 'casual';
    const s2 = m.p2_style || 'casual';
    const g1 = m.p1_game_system ? `<div style="font-size:11px;color:#888;margin-top:2px;">${escapeHTML(m.p1_game_system)}</div>` : '';
    const g2 = m.p2_game_system ? `<div style="font-size:11px;color:#888;margin-top:2px;">${escapeHTML(m.p2_game_system)}</div>` : '';
    const f1 = m.p1_narrative_faction ? `<div style="font-size:11px;color:#854F0B;margin-top:2px;">📖 ${escapeHTML(m.p1_narrative_faction)}</div>` : '';
    const f2 = m.p2_narrative_faction ? `<div style="font-size:11px;color:#854F0B;margin-top:2px;">📖 ${escapeHTML(m.p2_narrative_faction)}</div>` : '';
    const p1Profile = profileMap && profileMap[m.p1_id];
    const p2Profile = profileMap && profileMap[m.p2_id];
    const d1 = (p1Profile && p1Profile.discord_handle && p1Profile.show_discord !== false)
      ? `<div style="font-size:11px;color:#534AB7;margin-top:3px;">🎮 ${escapeHTML(p1Profile.discord_handle)}</div>` : '';
    const d2 = (p2Profile && p2Profile.discord_handle && p2Profile.show_discord !== false)
      ? `<div style="font-size:11px;color:#534AB7;margin-top:3px;">🎮 ${escapeHTML(p2Profile.discord_handle)}</div>` : '';
    return `<div class="tnf-match-card">
      <div class="tnf-match-player">
        <div class="tnf-match-avatar tnf-avatar--${s1}">${initials(m.p1_name)}</div>
        <div class="tnf-match-name">${escapeHTML(m.p1_name)}</div>
        <span class="tnf-badge tnf-badge--${s1}" style="margin-top:4px;display:inline-block;">${s1}</span>
        ${g1}${f1}${d1}
      </div>
      <div class="tnf-match-vs">
        <span class="tnf-vs-label">VS</span>
        <span class="tnf-table-num">T${tableNum}</span>
      </div>
      <div class="tnf-match-player">
        <div class="tnf-match-avatar tnf-avatar--${s2}">${initials(m.p2_name)}</div>
        <div class="tnf-match-name">${escapeHTML(m.p2_name)}</div>
        <span class="tnf-badge tnf-badge--${s2}" style="margin-top:4px;display:inline-block;">${s2}</span>
        ${g2}${f2}${d2}
      </div>
    </div>`;
  }

  /* ─────────────────────────────────────────────
     MATCHMAKING ALGORITHM
  ───────────────────────────────────────────── */
  async function runMatchmaking() {
    const btn = $('tnf-run-matchmaking-btn');
    btn.disabled = true;
    btn.textContent = 'Running…';

    try {
      // Load all non-league players for this week
      const players = await sbFetch(
        '/tnf_signups?week_key=eq.' + currentWeekKey + '&play_style=neq.league&order=created_at.asc'
      );

      if (!players || players.length < 2) {
        showToast('Need at least 2 players to run matchmaking');
        return;
      }

      // Load profiles to check accessibility flags
      const customerIds = players.map(p => p.customer_id).join(',');
      const profiles = await sbFetch('/tnf_player_profiles?customer_id=in.(' + customerIds + ')').catch(() => []);
      const accessMap = {};
      (profiles || []).forEach(pr => { accessMap[pr.customer_id] = pr.accessibility === true; });

      // Load recent pairings (last 4 weeks) to avoid repeat matchups
      const recentOpponents = {};
      const fourWeeksAgo = getWeekKey(-4);
      const recentPairings = await sbFetch(
        '/tnf_pairings?week_key=gte.' + fourWeeksAgo + '&week_key=neq.' + currentWeekKey
      );
      (recentPairings || []).forEach(p => {
        if (!recentOpponents[p.p1_id]) recentOpponents[p.p1_id] = [];
        if (!recentOpponents[p.p2_id]) recentOpponents[p.p2_id] = [];
        recentOpponents[p.p1_id].push(p.p2_id);
        recentOpponents[p.p2_id].push(p.p1_id);
      });

      // Shuffle then match by game system → play style, avoiding recent opponents
      const shuffle = arr => {
        for (let i = arr.length - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1));
          [arr[i], arr[j]] = [arr[j], arr[i]];
        }
        return arr;
      };

      const pool    = shuffle([...players]);
      const matched = new Set();
      const pairings = [];

      // ── Apply locked pairs first ──────────────────
      const playerIds = new Set(players.map(p => p.id));
      for (const lp of lockedPairs) {
        if (playerIds.has(lp.p1.id) && playerIds.has(lp.p2.id)
            && !matched.has(lp.p1.id) && !matched.has(lp.p2.id)) {
          matched.add(lp.p1.id);
          matched.add(lp.p2.id);
          pairings.push({ p1: lp.p1, p2: lp.p2 });
        }
      }

      function tryMatchGroup(subset) {
        const avail = subset.filter(p => !matched.has(p.id));
        while (avail.length >= 2) {
          const p1 = avail.shift();
          matched.add(p1.id);
          const recent = recentOpponents[p1.customer_id] || [];
          let idx = avail.findIndex(p => !recent.includes(p.customer_id));
          if (idx === -1) idx = 0;
          const p2 = avail.splice(idx, 1)[0];
          matched.add(p2.id);
          pairings.push({ p1, p2 });
        }
      }

      // ── Match within each game system first ──────
      // Group players by game_system_id
      const systemGroups = {};
      for (const p of pool) {
        const key = p.game_system_id || 'none';
        if (!systemGroups[key]) systemGroups[key] = [];
        systemGroups[key].push(p);
      }

      // Within each system:
      // 1. Narrative vs Narrative (different faction preferred)
      // 2. Competitive vs Competitive
      // 3. Casual vs Casual
      for (const group of Object.values(systemGroups)) {
        // Narrative — prefer different faction pairings
        const narrativePlayers = group.filter(p => p.play_style === 'narrative' && !matched.has(p.id));
        const narrativeAvail   = [...narrativePlayers];
        while (narrativeAvail.length >= 2) {
          const p1     = narrativeAvail.shift();
          matched.add(p1.id);
          const recent = recentOpponents[p1.customer_id] || [];
          // Prefer different faction, then avoid recent opponents
          let idx = narrativeAvail.findIndex(p =>
            p.narrative_faction !== p1.narrative_faction && !recent.includes(p.customer_id)
          );
          if (idx === -1) idx = narrativeAvail.findIndex(p => p.narrative_faction !== p1.narrative_faction);
          if (idx === -1) idx = narrativeAvail.findIndex(p => !recent.includes(p.customer_id));
          if (idx === -1) idx = 0;
          const p2 = narrativeAvail.splice(idx, 1)[0];
          matched.add(p2.id);
          pairings.push({ p1, p2 });
        }

        tryMatchGroup(group.filter(p => p.play_style === 'competitive'));
        tryMatchGroup(group.filter(p => p.play_style === 'casual'));
      }

      // ── Cross-style within same system for odd leftovers ──
      // Paired but both recorded as Casual
      // Narrative leftovers are NOT cross-paired with Casual/Competitive
      for (const group of Object.values(systemGroups)) {
        const nonNarrativeLeftover = group.filter(p => !matched.has(p.id) && p.play_style !== 'narrative');
        while (nonNarrativeLeftover.length >= 2) {
          const p1 = nonNarrativeLeftover.shift();
          const p2 = nonNarrativeLeftover.shift();
          matched.add(p1.id);
          matched.add(p2.id);
          // Override style to casual for cross-style pairs
          pairings.push({
            p1: { ...p1, play_style: 'casual' },
            p2: { ...p2, play_style: 'casual' },
          });
        }
        // Any single remaining player (including Narrative) stays unmatched
      }

      // ── Accessibility table assignment ────────────────
      // Identify pairings where at least one player needs accessibility
      const accessPairingIdxs = pairings
        .map((pair, idx) => ({
          idx,
          needs: accessMap[pair.p1.customer_id] || accessMap[pair.p2.customer_id],
        }))
        .filter(x => x.needs)
        .map(x => x.idx);

      // Build accessible table pool only if any accessibility players exist
      let accessTablePool = [];
      if (accessPairingIdxs.length > 0) {
        accessTablePool = [1, 5];
        if (pairings.length >= 12) accessTablePool.push(6, 10);
      }

      // Assign table numbers — accessibility pairings get pool slots first,
      // others get sequential numbers skipping reserved accessibility slots
      const reservedTables = new Set(accessTablePool);
      const tableAssignments = new Array(pairings.length);

      // Assign accessibility tables
      accessPairingIdxs.forEach((pairingIdx, i) => {
        tableAssignments[pairingIdx] = accessTablePool[i] !== undefined
          ? accessTablePool[i]
          : null; // overflow — will get a normal table below
      });

      // Assign sequential tables to everyone else, skipping reserved slots
      let nextTable = 1;
      for (let i = 0; i < pairings.length; i++) {
        if (tableAssignments[i] !== undefined && tableAssignments[i] !== null) continue;
        // Skip reserved accessibility slots
        while (reservedTables.has(nextTable)) nextTable++;
        tableAssignments[i] = nextTable++;
      }

      // Write pairings to DB in parallel
      await Promise.all(pairings.map(async ({ p1, p2 }, i) => {
        await sbFetch('/tnf_pairings', {
          method: 'POST',
          body: JSON.stringify({
            week_key:             currentWeekKey,
            p1_name:              p1.customer_name,
            p1_id:                p1.customer_id,
            p1_style:             p1.play_style,
            p1_game_system:       p1.game_system_name || null,
            p1_narrative_faction: p1.narrative_faction || null,
            p2_name:              p2.customer_name,
            p2_id:                p2.customer_id,
            p2_style:             p2.play_style,
            p2_game_system:       p2.game_system_name || null,
            p2_narrative_faction: p2.narrative_faction || null,
            table_number:         tableAssignments[i],
          }),
        });
        await Promise.all([
          sbFetch('/tnf_signups?id=eq.' + p1.id, { method: 'PATCH', body: JSON.stringify({ matched: true }) }),
          sbFetch('/tnf_signups?id=eq.' + p2.id, { method: 'PATCH', body: JSON.stringify({ matched: true }) }),
        ]);
      }));

      await saveEventSettings({ matched: true });
      setStatusPill('matched');
      showToast('Pairings done! ' + pairings.length + ' games set ⚔️');
      loadAdminTab();

    } catch (e) {
      console.error('TNF matchmaking error:', e);
      showToast('Matchmaking failed — check console');
    } finally {
      btn.disabled = false;
      btn.textContent = 'Run Matchmaking';
    }
  }

  /* ─────────────────────────────────────────────
     ADMIN TAB
  ───────────────────────────────────────────── */
  async function loadAdminTab() {
    if (!isAdmin) return;

    const settings = await loadEventSettings();
    signupsOpen = settings.signups_open;
    const matched = settings.matched;

    $('tnf-admin-status-text').textContent = signupsOpen ? 'Open' : 'Closed';
    const toggleBtn = $('tnf-toggle-signups-btn');
    if (toggleBtn) toggleBtn.textContent = signupsOpen ? 'Close Sign-ups' : 'Open Sign-ups';
    updateOverrideIndicator(settings.admin_override === true);

    // Add player card — lock it when pairings have run
    const addCard = $('tnf-admin-add-card');
    if (addCard) {
      addCard.style.opacity = matched ? '0.45' : '1';
      addCard.style.pointerEvents = matched ? 'none' : 'auto';
      const existingNote = document.getElementById('tnf-admin-add-locked-note');
      if (!existingNote && matched) {
        const note = document.createElement('p');
        note.id = 'tnf-admin-add-locked-note';
        note.style.cssText = 'font-size:.8125rem;color:#854F0B;margin-top:8px;';
        note.textContent = 'Undo pairings below to add or remove players.';
        addCard.appendChild(note);
      } else if (existingNote && !matched) {
        existingNote.remove();
      }
    }

    // Players list — show delete buttons only when pairings not yet run
    const players = await sbFetch('/tnf_signups?week_key=eq.' + currentWeekKey + '&order=created_at.asc');
    const list = $('tnf-admin-players-list');
    if (!players || players.length === 0) {
      list.innerHTML = '<p style="color:#aaa;font-size:.875rem;padding:.5rem 0;">No players yet.</p>';
    } else {
      // Load profiles for game badges
      const customerIds = players.map(p => p.customer_id).join(',');
      const [profiles, gameSystems] = await Promise.all([
        sbFetch('/tnf_player_profiles?customer_id=in.(' + customerIds + ')').catch(() => []),
        sbFetch('/tnf_game_systems?active=eq.true').catch(() => []),
      ]);
      const profileMap = {};
      (profiles || []).forEach(pr => { profileMap[pr.customer_id] = pr; });
      const gameMap = {};
      (gameSystems || []).forEach(gs => { gameMap[gs.id] = gs.name; });

      list.innerHTML = players.map(p => playerRowHTML(p, !matched, profileMap[p.customer_id], gameMap)).join('');
      list.querySelectorAll('.tnf-delete-btn').forEach(btn => {
        btn.addEventListener('click', async () => {
          if (!confirm('Remove ' + btn.dataset.name + ' from this week?')) return;
          await sbFetch('/tnf_signups?id=eq.' + btn.dataset.id, { method: 'DELETE', prefer: 'return=minimal' });
          showToast('Player removed');
          loadAdminTab();
        });
      });
    }

    // Manual pairing card
    renderManualPairingCard(players, matched);

    // Game systems card
    loadGameSystems();

    // Manual-add game system dropdown
    loadManualGameSystems();

    // Matchmaking card — switches between Run and Undo/Re-run based on state
    const mmStatus = $('tnf-admin-matchmaking-status');
    const mmBtns   = $('tnf-admin-matchmaking-btns');
    const mmLabel  = $('tnf-admin-matchmaking-label');

    if (matched) {
      mmLabel.textContent = 'Pairings';
      mmStatus.style.display = 'block';
      mmStatus.textContent = 'Matchmaking has been run for this week.';
      mmBtns.innerHTML = `
        <div style="display:flex;gap:8px;margin-top:4px;">
          <button class="tnf-btn tnf-btn--secondary" id="tnf-undo-pairings-btn" style="flex:1;">Undo Pairings</button>
          <button class="tnf-btn tnf-btn--primary" id="tnf-repair-btn" style="flex:1;">Re-run Matchmaking</button>
        </div>`;
      document.getElementById('tnf-undo-pairings-btn').addEventListener('click', undoPairings);
      document.getElementById('tnf-repair-btn').addEventListener('click', runMatchmaking);
    } else {
      mmLabel.textContent = 'Matchmaking';
      mmStatus.style.display = 'none';
      mmBtns.innerHTML = `<button class="tnf-btn tnf-btn--primary" id="tnf-run-matchmaking-btn" style="margin-top:4px;">Run Matchmaking</button>`;
      document.getElementById('tnf-run-matchmaking-btn').addEventListener('click', runMatchmaking);
    }

    // Email card — permanently hidden. Pairing emails go out automatically
    // (Tuesday 9am PT) and manual sends live in OBG Tools' /app/tnf-admin,
    // which authenticates server-side.
    const emailCard = $('tnf-admin-email-card');
    if (emailCard) emailCard.style.display = 'none';

    // Dropout notification card
    const dropouts = await sbFetch('/tnf_dropouts?week_key=eq.' + currentWeekKey + '&order=created_at.desc').catch(() => []);
    const dropoutCard = $('tnf-admin-dropout-card');
    const dropoutList = $('tnf-admin-dropout-list');
    if (dropoutCard && dropoutList) {
      // Filter out dropouts where the player has since re-registered
      const activePlayerIds = new Set((players || []).map(p => p.customer_id));
      const activeDropouts = (dropouts || []).filter(d => !activePlayerIds.has(d.customer_id));
      if (activeDropouts.length > 0) {
        dropoutCard.style.display = 'block';
        dropoutList.innerHTML = activeDropouts.map(d => `
          <div class="tnf-dropout-row">
            <div class="tnf-dropout-info">
              <span class="tnf-player-name">${escapeHTML(d.customer_name)}</span>
              ${d.had_pairing
                ? `<span class="tnf-dropout-note">Was paired with <strong>${escapeHTML(d.opponent_name)}</strong> at Table ${d.table_number}</span>`
                : `<span class="tnf-dropout-note">Had not yet been paired</span>`}
            </div>
          </div>`).join('');
      } else {
        dropoutCard.style.display = 'none';
      }
    }
  }

  /* ─────────────────────────────────────────────
     ADMIN — MANUAL PAIRING UI
  ───────────────────────────────────────────── */
  function renderManualPairingCard(players, matched) {
    const card = $('tnf-admin-manual-pair-card');
    if (!card) return;

    card.style.opacity       = matched ? '0.45' : '1';
    card.style.pointerEvents = matched ? 'none' : 'auto';

    // Build available player options (exclude league + already-locked players)
    const lockedIds = new Set(lockedPairs.flatMap(lp => [lp.p1.id, lp.p2.id]));
    const available = (players || []).filter(p => p.play_style !== 'league' && !lockedIds.has(p.id));

    const opts = available.map(p =>
      `<option value="${p.id}">${p.customer_name} (${p.play_style})</option>`
    ).join('');
    const placeholder = '<option value="">— select player —</option>';

    const sel1 = $('tnf-manual-pair-p1');
    const sel2 = $('tnf-manual-pair-p2');
    if (sel1) sel1.innerHTML = placeholder + opts;
    if (sel2) sel2.innerHTML = placeholder + opts;

    // Render locked pairs list
    const listEl = $('tnf-locked-pairs-list');
    if (!listEl) return;

    if (lockedPairs.length === 0) {
      listEl.innerHTML = '<p class="tnf-manual-pair-empty">No manual pairings set — auto-matchmaking handles everyone.</p>';
    } else {
      listEl.innerHTML = lockedPairs.map((lp, idx) => `
        <div class="tnf-locked-pair-row">
          <div class="tnf-locked-pair-players">
            <span class="tnf-locked-pair-name">
              <span class="tnf-avatar tnf-avatar--${lp.p1.play_style}" style="width:24px;height:24px;font-size:.6875rem;display:inline-flex;flex-shrink:0;">${initials(lp.p1.customer_name)}</span>
              ${escapeHTML(lp.p1.customer_name)}
            </span>
            <span class="tnf-locked-vs">VS</span>
            <span class="tnf-locked-pair-name">
              <span class="tnf-avatar tnf-avatar--${lp.p2.play_style}" style="width:24px;height:24px;font-size:.6875rem;display:inline-flex;flex-shrink:0;">${initials(lp.p2.customer_name)}</span>
              ${escapeHTML(lp.p2.customer_name)}
            </span>
          </div>
          <button class="tnf-delete-btn tnf-unlock-btn" data-idx="${idx}" title="Remove pairing">✕</button>
        </div>`).join('');

      listEl.querySelectorAll('.tnf-unlock-btn').forEach(btn => {
        btn.addEventListener('click', () => {
          lockedPairs.splice(Number(btn.dataset.idx), 1);
          renderManualPairingCard(players, matched);
        });
      });
    }
  }

  const lockPairBtn = $('tnf-lock-pair-btn');
  if (lockPairBtn) {
    lockPairBtn.addEventListener('click', async () => {
      const sel1 = $('tnf-manual-pair-p1');
      const sel2 = $('tnf-manual-pair-p2');
      const p1Id = sel1 ? sel1.value : '';
      const p2Id = sel2 ? sel2.value : '';
      if (!p1Id || !p2Id)  { showToast('Select two players to pair'); return; }
      if (p1Id === p2Id)   { showToast('Select two different players'); return; }

      lockPairBtn.disabled = true;
      lockPairBtn.textContent = 'Locking…';
      try {
        const players = await sbFetch('/tnf_signups?week_key=eq.' + currentWeekKey + '&order=created_at.asc');
        const p1 = players.find(p => String(p.id) === p1Id);
        const p2 = players.find(p => String(p.id) === p2Id);
        if (!p1 || !p2) { showToast('Could not find players'); return; }
        lockedPairs.push({ p1, p2 });
        showToast(p1.customer_name + ' ⚔️ ' + p2.customer_name + ' locked in');
        renderManualPairingCard(players, false);
      } catch (e) {
        showToast('Failed to lock pairing — check console');
        console.error('TNF lock pair error:', e);
      } finally {
        lockPairBtn.disabled = false;
        lockPairBtn.textContent = 'Lock Pairing';
      }
    });
  }

  /* ─────────────────────────────────────────────
     ADMIN — UNDO PAIRINGS
  ───────────────────────────────────────────── */
  async function undoPairings() {
    if (!confirm('Undo all pairings for this week? Players will return to the sign-up pool and you can re-run matchmaking.')) return;
    const btn = document.getElementById('tnf-undo-pairings-btn');
    if (btn) { btn.disabled = true; btn.textContent = 'Undoing…'; }
    try {
      await sbFetch('/tnf_pairings?week_key=eq.' + currentWeekKey, { method: 'DELETE', prefer: 'return=minimal' });
      await sbFetch('/tnf_signups?week_key=eq.' + currentWeekKey, {
        method: 'PATCH',
        prefer: 'return=minimal',
        body: JSON.stringify({ matched: false }),
      });
      const scheduledOpen = scheduledSignupsOpen();
      await saveEventSettings({ matched: false, signups_open: scheduledOpen, admin_override: false });
      signupsOpen = scheduledOpen;
      setStatusPill(scheduledOpen ? 'open' : 'closed');
      showToast('Pairings undone — ready to re-pair');
      lockedPairs = [];
      loadAdminTab();
    } catch (e) {
      showToast('Undo failed — check console');
      console.error('TNF undo pairings error:', e);
    }
  }

  /* ─────────────────────────────────────────────
     ADMIN — MANUAL ADD PLAYER
  ───────────────────────────────────────────── */
  let manualSelectedStyle = null;
  let manualLinkedCustomerId = null;

  async function loadManualGameSystems() {
    const sel = $('tnf-manual-game');
    if (!sel) return;
    try {
      const systems = await sbFetch('/tnf_game_systems?active=eq.true&order=display_order.asc,name.asc');
      sel.innerHTML = '<option value="">— select a game system —</option>' +
        (systems || []).map(gs => `<option value="${gs.id}" data-name="${escapeHTML(gs.name)}">${escapeHTML(gs.name)}</option>`).join('');
    } catch (e) {
      sel.innerHTML = '<option value="">Could not load game systems</option>';
      console.error('TNF loadManualGameSystems error:', e);
    }
  }

  function setManualLookupStatus(text, kind) {
    const el = $('tnf-manual-lookup-status');
    if (!el) return;
    if (!text) { el.style.display = 'none'; el.textContent = ''; return; }
    el.style.display = 'block';
    el.textContent = text;
    el.style.color = kind === 'ok' ? '#2E7D32' : kind === 'error' ? '#E24B4A' : '#aaa';
  }

  function resetManualLink() {
    manualLinkedCustomerId = null;
    setManualLookupStatus('', null);
  }

  document.querySelectorAll('[data-manual-style]').forEach(btn => {
    btn.addEventListener('click', () => {
      const s = btn.dataset.manualStyle;
      manualSelectedStyle = s;
      document.querySelectorAll('[data-manual-style]').forEach(b => {
        b.className = 'tnf-style-btn';
        if (b.dataset.manualStyle === s) b.classList.add('tnf-style-btn--selected-' + s);
      });
    });
  });

  const manualEmailInput = $('tnf-manual-email');
  if (manualEmailInput) {
    manualEmailInput.addEventListener('input', resetManualLink);
  }

  const manualLookupBtn = $('tnf-manual-lookup-btn');
  if (manualLookupBtn) {
    manualLookupBtn.addEventListener('click', async () => {
      const email = (manualEmailInput ? manualEmailInput.value : '').trim();
      if (!email) { setManualLookupStatus('Enter an email to look up', 'error'); return; }
      manualLookupBtn.disabled = true;
      manualLookupBtn.textContent = 'Looking up…';
      try {
        const encoded = encodeURIComponent(email);
        const rows = await sbFetch('/tnf_player_profiles?notification_email=ilike.' + encoded + '&limit=1');
        if (!rows || rows.length === 0) {
          manualLinkedCustomerId = null;
          setManualLookupStatus('No profile found — add manually below', 'error');
          return;
        }
        const profile = rows[0];
        manualLinkedCustomerId = profile.customer_id;
        const nameInput = $('tnf-manual-name');
        if (nameInput) nameInput.value = profile.customer_name || '';
        if (profile.favourite_game_id) {
          const gameSel = $('tnf-manual-game');
          if (gameSel && [...gameSel.options].some(o => o.value === String(profile.favourite_game_id))) {
            gameSel.value = String(profile.favourite_game_id);
          }
        }
        setManualLookupStatus('Linked to ' + (profile.customer_name || 'profile'), 'ok');
      } catch (e) {
        manualLinkedCustomerId = null;
        setManualLookupStatus('Lookup failed — check console', 'error');
        console.error('TNF manual lookup error:', e);
      } finally {
        manualLookupBtn.disabled = false;
        manualLookupBtn.textContent = 'Look up';
      }
    });
  }

  const manualAddBtn = $('tnf-manual-add-btn');
  if (manualAddBtn) {
    manualAddBtn.addEventListener('click', async () => {
      const nameInput = $('tnf-manual-name');
      const name = nameInput ? nameInput.value.trim() : '';
      const gameSel = $('tnf-manual-game');
      const gameSystemId   = gameSel ? gameSel.value : '';
      const gameSystemName = gameSel && gameSel.selectedIndex > 0
        ? gameSel.options[gameSel.selectedIndex].dataset.name : '';
      if (!name) { showToast('Please enter a player name'); return; }
      if (!manualSelectedStyle) { showToast('Please select a play style'); return; }
      if (!gameSystemId) { showToast('Please select a game system'); return; }
      manualAddBtn.disabled = true;
      manualAddBtn.textContent = 'Adding…';
      try {
        const customerId = manualLinkedCustomerId || ('manual_' + Date.now());
        await sbFetch('/tnf_signups', {
          method: 'POST',
          body: JSON.stringify({
            week_key:         currentWeekKey,
            customer_id:      String(customerId),
            customer_name:    name,
            play_style:       manualSelectedStyle,
            game_system_id:   parseInt(gameSystemId, 10),
            game_system_name: gameSystemName,
          }),
        });
        showToast(name + ' added as ' + manualSelectedStyle);
        nameInput.value = '';
        if (manualEmailInput) manualEmailInput.value = '';
        if (gameSel) gameSel.selectedIndex = 0;
        manualSelectedStyle = null;
        document.querySelectorAll('[data-manual-style]').forEach(b => b.className = 'tnf-style-btn');
        resetManualLink();
        loadAdminTab();
      } catch (e) {
        showToast('Failed to add player — check console');
        console.error('TNF manual add error:', e);
      } finally {
        manualAddBtn.disabled = false;
        manualAddBtn.textContent = 'Add Player';
      }
    });
  }

  const toggleBtn = $('tnf-toggle-signups-btn');
  if (toggleBtn) {
    toggleBtn.addEventListener('click', async () => {
      const newVal = !signupsOpen;
      await saveEventSettings({ signups_open: newVal, admin_override: true });
      signupsOpen = newVal;
      setStatusPill(newVal ? 'open' : 'closed');
      toggleBtn.textContent = newVal ? 'Close Sign-ups' : 'Open Sign-ups';
      $('tnf-admin-status-text').textContent = newVal ? 'Open' : 'Closed';
      updateOverrideIndicator(true);
      showToast('Sign-ups ' + (newVal ? 'opened' : 'closed') + ' (manual override)');
    });
  }

  const resetBtn = $('tnf-reset-week-btn');
  if (resetBtn) {
    resetBtn.addEventListener('click', async () => {
      if (!confirm('Reset ALL data for this week? This cannot be undone.')) return;
      await sbFetch('/tnf_signups?week_key=eq.' + currentWeekKey, { method: 'DELETE', prefer: 'return=minimal' });
      await sbFetch('/tnf_pairings?week_key=eq.' + currentWeekKey, { method: 'DELETE', prefer: 'return=minimal' });
      await saveEventSettings({ matched: false, signups_open: scheduledSignupsOpen(), admin_override: false });
      setStatusPill(scheduledSignupsOpen() ? 'open' : 'closed');
      showToast('Week reset');
      loadAdminTab();
    });
  }

  /* ─────────────────────────────────────────────
     ADMIN — GAME SYSTEMS
  ───────────────────────────────────────────── */
  async function loadGameSystems() {
    const listEl = $('tnf-game-systems-list');
    if (!listEl) return;
    listEl.innerHTML = '<p style="color:#aaa;font-size:13px;">Loading…</p>';

    try {
      const systems = await sbFetch('/tnf_game_systems?order=display_order.asc,name.asc');
      if (!systems || systems.length === 0) {
        listEl.innerHTML = '<p style="color:#aaa;font-size:13px;">No game systems yet.</p>';
        return;
      }
      listEl.innerHTML = systems.map(gs => `
        <div class="tnf-game-system-row" data-id="${gs.id}">
          <span class="tnf-game-system-name ${gs.active ? '' : 'tnf-game-system-name--inactive'}">${escapeHTML(gs.name)}</span>
          <div class="tnf-game-system-actions">
            <button class="tnf-gs-toggle-btn tnf-week-btn" data-id="${gs.id}" data-active="${gs.active}" title="${gs.active ? 'Deactivate' : 'Activate'}">
              ${gs.active ? 'Active' : 'Hidden'}
            </button>
            <button class="tnf-delete-btn tnf-gs-delete-btn" data-id="${gs.id}" data-name="${escapeHTML(gs.name)}" title="Delete">✕</button>
          </div>
        </div>`).join('');

      listEl.querySelectorAll('.tnf-gs-toggle-btn').forEach(btn => {
        btn.addEventListener('click', async () => {
          const newActive = btn.dataset.active === 'true' ? false : true;
          await sbFetch('/tnf_game_systems?id=eq.' + btn.dataset.id, {
            method: 'PATCH',
            prefer: 'return=minimal',
            body: JSON.stringify({ active: newActive }),
          });
          loadGameSystems();
        });
      });

      listEl.querySelectorAll('.tnf-gs-delete-btn').forEach(btn => {
        btn.addEventListener('click', async () => {
          if (!confirm('Delete "' + btn.dataset.name + '"? This cannot be undone.')) return;
          await sbFetch('/tnf_game_systems?id=eq.' + btn.dataset.id, { method: 'DELETE', prefer: 'return=minimal' });
          showToast(btn.dataset.name + ' removed');
          loadGameSystems();
        });
      });
    } catch (e) {
      listEl.innerHTML = '<p style="color:#E24B4A;font-size:13px;">Failed to load game systems.</p>';
      console.error('TNF loadGameSystems error:', e);
    }
  }

  const addGameBtn = $('tnf-add-game-btn');
  if (addGameBtn) {
    addGameBtn.addEventListener('click', async () => {
      const input = $('tnf-new-game-name');
      const name = input ? input.value.trim() : '';
      if (!name) { showToast('Enter a game system name'); return; }
      addGameBtn.disabled = true;
      addGameBtn.textContent = 'Adding…';
      try {
        await sbFetch('/tnf_game_systems', {
          method: 'POST',
          body: JSON.stringify({ name, display_order: 999, active: true }),
        });
        input.value = '';
        showToast(name + ' added');
        loadGameSystems();
      } catch (e) {
        showToast('Failed to add — name may already exist');
        console.error('TNF addGame error:', e);
      } finally {
        addGameBtn.disabled = false;
        addGameBtn.textContent = '+ Add Game System';
      }
    });
  }


  /* ─────────────────────────────────────────────
     PROFILE — AUTO-CREATE / UPDATE ON PAGE LOAD
  ───────────────────────────────────────────── */
  async function ensureProfile() {
    try {
      const now = new Date().toISOString();
      const existing = await sbFetch(
        '/tnf_player_profiles?customer_id=eq.' + customerId + '&limit=1'
      );

      if (existing && existing.length > 0) {
        // Update last_seen_at on every visit
        await sbFetch('/tnf_player_profiles?customer_id=eq.' + customerId, {
          method: 'PATCH',
          prefer: 'return=minimal',
          body: JSON.stringify({
            customer_name: customerName,
            last_seen_at:  now,
          }),
        });
      } else {
        // Create profile with Shopify data — notify_email off by default
        await sbFetch('/tnf_player_profiles', {
          method: 'POST',
          body: JSON.stringify({
            customer_id:        String(customerId),
            customer_name:      customerName,
            notification_email: customerEmail || null,
            notify_email:       false,
            event_signups_count: 0,
            first_seen_at:      now,
            last_seen_at:       now,
          }),
        });
      }
    } catch (e) {
      // Silently fail — profile creation is non-critical
      console.warn('TNF: ensureProfile failed', e);
    }
  }

  /* ─────────────────────────────────────────────
     BOOT
  ───────────────────────────────────────────── */
  window.addEventListener('beforeunload', e => {
    if (lockedPairs.length > 0) {
      e.preventDefault();
      e.returnValue = '';
    }
  });

  initSignupTab();
  if (customerId) ensureProfile();

})();
